<template>
  <button type="button" class="md-kpi" :class="[{ 'md-kpi--tappable': tappable }, tone ? `md-kpi--${tone}` : '']" @click="$emit('open')">
    <span class="md-kpi__label">{{ label }}</span>
    <span class="md-kpi__value">{{ value }}</span>
    <span v-if="delta != null" class="md-kpi__delta" :class="deltaClass">
      <i :class="delta >= 0 ? 'fa-solid fa-arrow-trend-up' : 'fa-solid fa-arrow-trend-down'"></i>
      {{ Math.abs(delta).toFixed(1) }}%
    </span>
    <span v-else-if="sub" class="md-kpi__sub">{{ sub }}</span>
    <span v-else class="md-kpi__sub">&nbsp;</span>
  </button>
</template>

<script>
export default {
  name: 'MdKpiCard',
  props: {
    label: { type: String, required: true },
    value: { type: String, required: true },
    // % change vs previous period; null hides the trend line
    delta: { type: Number, default: null },
    // true when a LOWER value is good (e.g. withdrawals)
    invert: { type: Boolean, default: false },
    sub: { type: String, default: null },
    tappable: { type: Boolean, default: false },
    // Semantic accent: deposit | withdraw | ftd | std | clients | conv
    tone: { type: String, default: null },
  },
  computed: {
    deltaClass() {
      const good = this.invert ? this.delta < 0 : this.delta >= 0;
      return good ? 'md-kpi__delta--up' : 'md-kpi__delta--down';
    },
  },
};
</script>

<style scoped>
.md-kpi {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 0.7rem 0.85rem;
  text-align: left;
  background: var(--md-card-bg, #fff);
  border: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.08));
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(34, 41, 47, 0.06), 0 5px 12px rgba(34, 41, 47, 0.1);
  transition: transform 0.08s ease, box-shadow 0.15s ease;
}
.md-kpi--tappable:active {
  transform: scale(0.975);
  box-shadow: 0 1px 2px rgba(34, 41, 47, 0.08);
}
.dark-layout .md-kpi {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.28), 0 7px 16px rgba(0, 0, 0, 0.22);
}
.md-kpi__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--md-muted, #8a919c);
}
.md-kpi__value {
  font-size: 16.5px;
  font-weight: 700;
  line-height: 1.25;
  font-variant-numeric: tabular-nums;
  color: var(--md-strong, #24292f);
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.md-kpi__delta {
  font-size: 11.5px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.md-kpi__delta--up { color: #1f9d61; }
.md-kpi__delta--down { color: #d64545; }
.md-kpi__sub {
  font-size: 11.5px;
  color: var(--md-muted, #8a919c);
}

/* Semantic accents: soft tinted card + colored value, left accent bar */
.md-kpi--deposit { background: rgba(40, 199, 111, 0.06); border-left: 3px solid #28c76f; }
.md-kpi--deposit .md-kpi__value { color: #1f9d61; }
.md-kpi--withdraw { background: rgba(234, 84, 85, 0.06); border-left: 3px solid #ea5455; }
.md-kpi--withdraw .md-kpi__value { color: #d64545; }
.md-kpi--ftd { background: rgba(115, 103, 240, 0.07); border-left: 3px solid #7367f0; }
.md-kpi--ftd .md-kpi__value { color: #6a5fe0; }
.md-kpi--std { background: rgba(0, 207, 232, 0.07); border-left: 3px solid #00cfe8; }
.md-kpi--std .md-kpi__value { color: #0aa8bd; }
.md-kpi--clients { background: rgba(0, 158, 217, 0.06); border-left: 3px solid #009ed9; }
.md-kpi--clients .md-kpi__value { color: #0f89b8; }
.md-kpi--conv { background: rgba(255, 159, 67, 0.07); border-left: 3px solid #ff9f43; }
.md-kpi--conv .md-kpi__value { color: #c47b28; }
/* TXF (taxes): deeper teal — greener than the STD cyan so the two neighbours
   stay apart, and the same family as the PC dashboards' TXF card. */
.md-kpi--txf { background: rgba(20, 184, 166, 0.07); border-left: 3px solid #14b8a6; }
.md-kpi--txf .md-kpi__value { color: #0d9488; }

.dark-layout .md-kpi {
  --md-card-bg: #283046;
  --md-card-border: #3b4253;
  --md-strong: #d0d2d6;
  --md-muted: #8a919c;
}
.dark-layout .md-kpi--deposit { background: rgba(40, 199, 111, 0.1); }
.dark-layout .md-kpi--deposit .md-kpi__value { color: #48d989; }
.dark-layout .md-kpi--withdraw { background: rgba(234, 84, 85, 0.1); }
.dark-layout .md-kpi--withdraw .md-kpi__value { color: #f08182; }
.dark-layout .md-kpi--ftd { background: rgba(115, 103, 240, 0.12); }
.dark-layout .md-kpi--ftd .md-kpi__value { color: #9d94f5; }
.dark-layout .md-kpi--std { background: rgba(0, 207, 232, 0.1); }
.dark-layout .md-kpi--std .md-kpi__value { color: #4fd7e8; }
.dark-layout .md-kpi--clients { background: rgba(0, 158, 217, 0.1); }
.dark-layout .md-kpi--clients .md-kpi__value { color: #4fc3e8; }
.dark-layout .md-kpi--conv { background: rgba(255, 159, 67, 0.12); }
.dark-layout .md-kpi--conv .md-kpi__value { color: #ffb976; }
.dark-layout .md-kpi--txf { background: rgba(20, 184, 166, 0.12); }
.dark-layout .md-kpi--txf .md-kpi__value { color: #5eead4; }
</style>
